import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, AlertCircle } from 'lucide-react';
import ApiService from '../services/apiService';

export default function SupportChatWidget({ userId, isAdmin = false, currentAdminId = null, isOpen, setIsOpen, quickReplies = [], setQuickReplies, theme, isRTL = false }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [socket, setSocket] = useState(null);
    const [adminThreads, setAdminThreads] = useState([]);
    const [activeUserThread, setActiveUserThread] = useState(null);
    const messagesEndRef = useRef(null);

    // Auto scroll
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    // Fetch user history
    useEffect(() => {
        if (isOpen && userId && !isAdmin) {
            ApiService.get(`/support/${userId}/history`).then(data => {
                setMessages(data || []);
            }).catch(console.error);
        }
    }, [isOpen, userId, isAdmin]);

    // Admin fetch threads
    useEffect(() => {
        if (isOpen && isAdmin && currentAdminId && !activeUserThread) {
            ApiService.get(`/support/admin/threads?admin_id=${currentAdminId}`).then(data => {
                setAdminThreads(data || []);
            }).catch(console.error);
        }
    }, [isOpen, isAdmin, currentAdminId, activeUserThread]);

    // Admin fetch thread history
    useEffect(() => {
        if (isAdmin && activeUserThread) {
            ApiService.get(`/support/${activeUserThread}/history`).then(data => {
                setMessages(data || []);
            }).catch(console.error);
        }
    }, [isAdmin, activeUserThread]);

    // Setup websocket for normal user (or admin listening to specific thread maybe, but for now normal user)
    useEffect(() => {
        if (isOpen && !isAdmin && userId) {
            const ws = new WebSocket(`ws://localhost:8009/support/ws/${userId}`);

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                setMessages(prev => [...prev, { role: data.role, message: data.message, timestamp: new Date() }]);
            };

            setSocket(ws);
            return () => ws.close();
        }
    }, [isOpen, isAdmin, userId]);

    const handleSend = async (overrideMsg = null) => {
        const isOverride = typeof overrideMsg === 'string';
        const textToSend = isOverride ? overrideMsg : input;

        if (!textToSend.trim()) return;

        if (isAdmin && activeUserThread && currentAdminId) {
            const tempMsg = { role: 'Admin', message: textToSend, timestamp: new Date() };
            setMessages(prev => [...prev, tempMsg]);
            const payload = {
                user_id: activeUserThread,
                message: textToSend,
                admin_id: currentAdminId
            };
            if (!isOverride) setInput("");
            try {
                await ApiService.post('/support/admin/reply', payload);
            } catch (e) {
                console.error(e);
            }
        } else if (!isAdmin && socket && socket.readyState === WebSocket.OPEN) {
            socket.send(textToSend);
            setMessages(prev => [...prev, { role: 'User', message: textToSend, timestamp: new Date() }]);
            if (!isOverride) setInput("");
            if (setQuickReplies) setQuickReplies([]); // clear pills after sending
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className={`fixed bottom-6 ltr:right-6 rtl:left-6 p-4 rounded-full shadow-xl transition-all hover:scale-110 active:scale-95 z-50 flex items-center justify-center border group ${theme === 'dark'
                    ? 'bg-[#2C3E50] hover:bg-[#34495E] text-[#F4EFEA] border-white/10'
                    : 'bg-white hover:bg-gray-50 text-[#1B263B] border-gray-200'
                    }`}>
                <MessageCircle size={28} />
                {isAdmin && adminThreads.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#C47A2C] w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center shadow-md animate-bounce">{adminThreads.length}</span>
                )}
            </button>
        );
    }

    return (
        <div className="fixed bottom-24 ltr:right-6 rtl:left-6 w-80 md:w-96 bg-white dark:bg-geo-navy border border-gray-200 dark:border-white/10 rounded-3xl shadow-2xl z-[9999] flex flex-col overflow-hidden transition-all duration-300 animate-slide-up" style={{ height: '550px' }}>
            {/* Header */}
            <div className="bg-geo-navy dark:bg-slate-900 border-b border-transparent dark:border-white/10 p-5 flex justify-between items-center z-10 w-full relative shrink-0">
                <div className="flex items-center gap-3">
                    <div className="bg-white/10 p-2 rounded-full">
                        <MessageCircle className="text-geo-copper" size={20} />
                    </div>
                    <span className="text-white font-bold tracking-wide">{isAdmin ? (activeUserThread ? `User #${activeUserThread}` : "Agent Inbox") : "Tech Support"}</span>
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin && activeUserThread && (
                        <button onClick={() => { setActiveUserThread(null); setMessages([]); }} className="text-geo-olive text-xs font-bold hover:text-white bg-geo-olive/20 px-2 py-1 rounded transition-colors mr-1">Inbox</button>
                    )}
                    <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-geo-sand/50 dark:bg-transparent custom-scrollbar relative">
                {isAdmin && !activeUserThread ? (
                    adminThreads.length === 0 ? <p className="text-gray-500 dark:text-gray-400 text-center mt-10 font-medium">No active support threads.</p> :
                        adminThreads.map(thread => (
                            <div key={thread.user_id} onClick={() => setActiveUserThread(thread.user_id)} className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:border-geo-copper/50 dark:hover:border-geo-copper/50 hover:shadow-md transition-all shadow-sm group">
                                <p className="text-geo-navy dark:text-white font-bold text-sm flex justify-between items-center">
                                    {thread.name}
                                    <span className="w-2 h-2 rounded-full bg-geo-olive group-hover:animate-ping"></span>
                                </p>
                                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1 truncate">{thread.email}</p>
                                <p className="text-gray-400 dark:text-gray-500 text-[10px] uppercase font-bold mt-3 tracking-wider border-t border-gray-100 dark:border-slate-700 pt-2 flex justify-between">
                                    <span>{thread.status}</span>
                                    <span>{new Date(thread.last_message).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </p>
                            </div>
                        ))
                ) : (
                    <>
                        {messages.length === 0 && (
                            <div className="flex flex-col items-center justify-center mt-10 opacity-60">
                                <MessageCircle size={40} className="text-gray-400 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium text-center">Hello! How can we assist<br />with your farming operations today?</p>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'User' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                <div className={`max-w-[85%] p-3.5 rounded-2xl text-sm shadow-sm ${m.role === 'User' ? 'bg-geo-olive text-white rounded-tr-sm' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-geo-navy dark:text-gray-200 rounded-tl-sm'}`}>
                                    <p className={`font-bold text-[10px] uppercase tracking-wider mb-1 ${m.role === 'User' ? 'text-white/60' : 'text-geo-copper'}`}>{m.role}</p>
                                    <p className="leading-relaxed">{m.message}</p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {/* Footer */}
            {(!isAdmin || activeUserThread) && (
                <div className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800 shrink-0 flex flex-col">
                    {/* Quick Replies */}
                    {quickReplies && quickReplies.length > 0 && !isAdmin && (
                        <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2 overflow-x-auto custom-scrollbar">
                            {quickReplies.map((reply, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSend(reply)}
                                    className="text-xs leading-snug font-bold bg-[#6B8F71]/10 text-[#6B8F71] border border-[#6B8F71]/30 hover:bg-[#6B8F71] hover:text-white px-3 py-2 rounded-xl transition-all text-left shadow-sm hover:shadow-md flex-1 min-w-[140px]"
                                >
                                    {reply}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="p-4">
                        <div className="flex bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full p-1.5 focus-within:border-geo-copper dark:focus-within:border-geo-copper focus-within:ring-2 focus-within:ring-geo-copper/10 transition-all shadow-inner">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                                placeholder="Type a message..."
                                className="flex-1 bg-transparent px-4 text-geo-navy dark:text-white font-medium outline-none text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            />
                            <button onClick={() => handleSend()} disabled={!input.trim()} className="bg-geo-navy dark:bg-geo-copper disabled:opacity-50 hover:bg-slate-800 dark:hover:bg-orange-700 p-2.5 rounded-full text-white transition-colors cursor-pointer shadow-md active:scale-95">
                                <Send size={16} className="ml-0.5 mt-0.5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
